import { z } from 'zod';
import { MediaJobStatus, MediaKind, Visibility } from './enums.ts';
import { brandedUuid } from './ids.ts';

/**
 * Media (generation) contracts — the typed shapes that flow across the media
 * seam. `MediaGenerateJobData` is the BullMQ payload the backend `MediaService`
 * enqueues once a `media_jobs` row is written and the `apps/jobs` worker consumes
 * to drive provider → download → e2 → `media_assets`. One source of truth so the
 * enqueuer and the worker agree on the wire shape (mirrors `MemoryExtractJobData`).
 *
 * The payload is deliberately MINIMAL: only `(accountId, jobId)` — the worker is
 * authoritative and loads the job + its segments from `@4f/db`, so a stale/replayed
 * message can never carry drifted parameters. `jobId` is the idempotency anchor
 * (passed as the BullMQ `jobId`), so a double-enqueue or retry collapses to one run.
 *
 * See docs/architecture/media-pipeline.md + docs/mvp-v1.0.0/phase-5-media.
 */
export const MediaGenerateJobData = z.object({
  accountId: brandedUuid('AccountId'),
  /** The `media_jobs` row to run — the worker's sole authoritative input + idempotency anchor. */
  jobId: brandedUuid('MediaJobId'),
});
export type MediaGenerateJobData = z.infer<typeof MediaGenerateJobData>;

/**
 * What a caller (agent tool, route, companion build) hands the media enqueuer.
 * A caller passes only the companion + prompt + (for video) the target duration —
 * the service reads the pinned `seed` + `reference_asset_id` from her `girlfriends`
 * row (character consistency is never a caller concern,
 * docs/architecture/character-consistency.md). Defined HERE (not as a backend-local
 * interface) because it crosses the api↔backend boundary — one Zod-validated source
 * of truth, branded ids, no drift.
 */
export const EnqueueMediaInput = z.object({
  accountId: brandedUuid('AccountId'),
  userId: brandedUuid('UserId'),
  girlfriendId: brandedUuid('GirlfriendId'),
  kind: MediaKind,
  /** The generation prompt (trimmed + bounded + prompt-gated by the service). */
  prompt: z.string(),
  /** Video only: requested clip length (ms); fanned into `⌈target / maxSegment⌉` segments. */
  targetDurationMs: z.number().optional(),
});
export type EnqueueMediaInput = z.infer<typeof EnqueueMediaInput>;

/** The handle returned once a job is reserved + written + enqueued. */
export const MediaJobView = z.object({
  jobId: brandedUuid('MediaJobId'),
  kind: MediaKind,
  status: MediaJobStatus,
  /** 1 for an image; N for a chained video. */
  segmentTotal: z.number().int().nonnegative(),
  /** Credits reserved for this job (micros), settled to the metered actual on completion. */
  creditsCostMicros: z.number().nonnegative(),
});
export type MediaJobView = z.infer<typeof MediaJobView>;

/**
 * A finished, stored media asset as the Artifacts gallery renders it. `url` is a
 * ready-to-fetch GET — a SHORT-TTL presigned URL for a `private` asset (the MVP
 * default; the provider URL is never exposed, only the e2 key is signed), or
 * `null` when the object could not be signed. Defined HERE (not backend-local)
 * because the companion gallery query crosses the api↔backend boundary — one
 * Zod-validated source of truth, branded ids, no drift (mirrors {@link MediaJobView}).
 */
export const MediaAssetView = z.object({
  assetId: brandedUuid('MediaAssetId'),
  /**
   * The job that produced it — lets the client match a live inline placeholder to
   * its asset. `null` for a pre-generated prebuilt-catalog asset (no user job).
   */
  jobId: brandedUuid('MediaJobId').nullable(),
  girlfriendId: brandedUuid('GirlfriendId'),
  kind: MediaKind,
  /** Short-TTL presigned GET URL (private) or public URL; `null` when unsignable. */
  url: z.string().nullable(),
  visibility: Visibility,
  contentType: z.string(),
  width: z.number().int().nonnegative(),
  height: z.number().int().nonnegative(),
  /** Null for an image; clip length (ms) for a video. */
  durationMs: z.number().int().nonnegative().nullable(),
  /**
   * VIDEO only: a presigned GET for the poster still a tile shows before
   * playback (`MediaTile`'s `src` contract). `null` for an image, for a video
   * whose poster extract failed, and for a poster that could not be signed — a
   * tile without one degrades to its skeleton, it never fails the listing.
   */
  posterUrl: z.string().nullable(),
  /** ISO-8601 creation timestamp; the client renders it in the user's timezone. */
  createdAt: z.string(),
});
export type MediaAssetView = z.infer<typeof MediaAssetView>;

/**
 * The realtime `media_job` frame — the client-facing projection of the worker's
 * `MediaProgressEvent` (`apps/jobs/src/media/ports.ts`). The media worker
 * publishes it to the account's Dragonfly channel, `apps/api` relays it over
 * `/ws`, and the client matches `jobId` against its live inline placeholder
 * (docs/architecture/media-pipeline.md § "Progress reporting").
 *
 * Defined HERE, once, because it crosses jobs → api → frontend: the publisher
 * serializes THIS schema and the client re-validates against it, so a field can
 * never drift on one side and be silently dropped on the other. (The client half
 * was hand-written with no producer at all — every event was dropped — which is
 * exactly the "typed twice ⇒ silently dead" failure this shared shape prevents.)
 *
 * Ids stay plain non-empty strings rather than `brandedUuid`: a frame is
 * UNTRUSTED input at the client boundary and only needs a handle to match a
 * placeholder — a strict uuid shape would drop an otherwise-actionable frame.
 */
export const MediaJobEvent = z.object({
  type: z.literal('media_job'),
  jobId: z.string().min(1),
  girlfriendId: z.string().min(1),
  status: MediaJobStatus,
  /** Present once the job reaches `done` — the stored `media_assets` id. */
  assetId: z.string().min(1).optional(),
  /** Video progress (a `segment_done` beat): segments produced so far, and in total. */
  segmentDone: z.number().int().nonnegative().optional(),
  segmentTotal: z.number().int().nonnegative().optional(),
  /** Why a terminal `failed` / `rejected` / `held` job ended — surfaced non-fatally. */
  reason: z.string().optional(),
});
export type MediaJobEvent = z.infer<typeof MediaJobEvent>;
